/**
 * "What the DM saw" — `outcome` and `situation`, behind a disclosure.
 *
 * A chapter used to render four labelled boxes: Narration, Your Action, Outcome, Situation. Three
 * of those are prose about the same events, and showing all three is the main reason such a
 * chapter reads like a database row rather than a scene. `narration` is the story; `outcome`
 * and `situation` are the DM's working notes.
 *
 * **the story feed's "read real chapters before committing" checkbox is now answered, and the answer was
 * that they were the same text.** The first chapter the deployed stack produced opened its
 * narration with `outcome` copied byte for byte, so the disclosure was showing the player a
 * shorter version of the scene directly above it. The fix was on the backend — `resolve` now
 * asks for terse notes rather than prose, and the narration brief says not to reuse
 * their wording (`docs/engine/narration.md` §5).
 *
 * This file's share of that fix is register: notes are set as notes — monospace, tight, one
 * short block each — because prose styling on a two-line fragment is what made them read as a
 * worse retelling rather than as the DM's margin. The notes were **English** in every story
 * until 2026-08-15, which made a German label sit over an English body; the amendment in
 * `docs/engine/narration.md` §5 found that terseness rather than English was what kept them uncopyable,
 * so they now arrive in the story's locale like everything else on this screen.
 *
 * `<details>` rather than a state hook, deliberately: it is keyboard-operable, screen-reader
 * announced and findable by in-page search when closed, all of which the accessibility work would otherwise owe.
 */
import { useT } from '@/i18n/context';

interface DmNotesProps {
  outcome: string;
  situation: string;
}

export function DmNotes({ outcome, situation }: DmNotesProps) {
  const t = useT();
  const hasOutcome = outcome.trim().length > 0;
  const hasSituation = situation.trim().length > 0;
  if (!hasOutcome && !hasSituation) return null;

  return (
    <details className="group mt-6">
      <summary
        className="text-fg-faint hover:text-fg-muted cursor-pointer list-none font-mono tracking-wide select-none marker:content-['']"
        style={{ fontSize: 'var(--text-meta)' }}
      >
        <span aria-hidden="true" className="inline-block w-4 group-open:hidden">
          ▸
        </span>
        <span aria-hidden="true" className="hidden w-4 group-open:inline-block">
          ▾
        </span>
        {t('ui.chapter.what_the_dm_saw')}
      </summary>

      {/*
        No "Outcome:" / "Situation:" captions — labelled boxes are the shape this component
        exists to get away from, and `ChapterScene.test.tsx` asserts those four words never
        appear. The two notes are told apart by weight instead: what happened, then where it
        stands, the second one dimmer.
      */}
      <div className="border-rule mt-3 space-y-2 border-l pl-4 font-mono leading-snug">
        {hasOutcome ? (
          <p className="text-fg-muted" style={{ fontSize: 'var(--text-meta)' }}>
            {outcome}
          </p>
        ) : null}
        {hasSituation ? (
          <p className="text-fg-faint" style={{ fontSize: 'var(--text-meta)' }}>
            {situation}
          </p>
        ) : null}
      </div>
    </details>
  );
}
